import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {BP_Status, State, USER_SELECTION_STATUS} from '../store/state';
import {BpmnViewerService} from '../services/bpmn-viewer.service';
import {DimNodesService} from '../bpmn/services/dim-nodes.service';
import {ExecutionPath} from '../models/execution-path';
import {
  selectBpmnProcessXml,
  selectExecutionPath,
  selectGetBpmnXmlParsingStatus,
  selectGuidedBoxDataById,
  selectGuidedBoxDataByIds,
  selectNodeSelectionMode,
  selectNodesNamesSuffixes, selectUserSelectionInProgress
} from '../store/selectors';
import {
  MarkBpmnXmlParsingCompletedAction,
  MarkBpmnXmlParsingInProgressAction,
  UpdateUserSelectionStatusAction
} from '../store';
import {EventType, InjectionNames} from '@ispm/shared/bpmn-io/bpmn-js';
import {Subject} from 'rxjs/Subject';
import {takeUntil} from 'rxjs/operators';
import {combineLatest} from 'rxjs/observable/combineLatest';
import {BpmnGuidedBoxService} from '@ispm-client/modules/process-executor/services/bpmn-guided-box.service';
import {Observable} from 'rxjs';
import {GuideBox, Shape} from 'diagram-js/lib/model';
import {TreeNavigator} from '@ispm/shared/bpmn-io/services/tree-navigator';
import {Node} from '../models/node';
import {ExecutedPathUtils, UserClicksElementsService} from '@ispm-client/modules/process-executor/services';
import {SetErrorAction} from '@ispm/shared/store/actions/error.actions';
import {failedToFindElementMessage, failedToLoadGuideboxesMessage} from '../models/constants';
import * as _ from 'lodash';
import {NodeType} from '@ispm/shared/bpmn-io/node-type';
import {ModelingUtil} from '@ispm/shared/bpmn-io/modeling-util';
import {TreePathNavigator} from '@ispm-client/modules/process-executor/bpmn/services/tree-path-navigator';
import {NodeSelectionMode} from "@ispm-client/modules/process-executor/models/tree-navigation-configuration";
import {MandatoryNoteVisitedAction} from "@ispm-client/modules/process-executor/store/actions/mandatory-note-visited.actions";
import {ZoomService} from '@ispm-client/modules/process-executor/bpmn/services/zoom-service';
import {TreeToScreenFitter} from '@ispm/shared/bpmn-io/services/tree-to-screen-fitter';
import {selectTreatmentNodeDataById} from '@ispm-client/modules/pathways/store';
import {Message, MessageService} from "@wuitk/angular/components/notification";
import {GuidedBoxPayload} from "@ispm/shared/classes/guided-box-payload.class";
import {TestService} from '@ispm/dashboard/test/services/test.service';

@Component({
  selector: 'ispm-bpmn-viewer',
  templateUrl: './bpmn-viewer.component.html',
  styleUrls: ['./bpmn-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BpmnViewerComponent implements AfterViewInit, OnDestroy, OnInit {
  protected viewer;
  protected executionPath: ExecutionPath;
  protected destroyed$: Subject<any> = new Subject();
  private previousExecutionPath: ExecutionPath;
  private firstLoad: boolean = true;
  private testConfiguration: any;
  private treatmentNotesGuideBox: boolean;
  public spinnerActive = false;
  public userSelectionInProgress : Observable<boolean> = Observable.of(false);
  public showMultipleGuideBox = false;
  public messageCount: number = 1;
  public readonly GUIDED_BOX_CHANNEL: string = BpmnGuidedBoxService.GuidedBoxChannel;
  public readonly BANNER_LOCAL: string = 'banner-local';
  readonly executedShapeCssClass = 'executedMark';
  readonly selectionRequiredCssClass = 'selectionRequiredMark';

  readonly addCssClassMethodName = 'addClass';
  readonly removeCssClassMethodName = 'removeClass';
  @Input() hideBpmnLogo: boolean = true;
  protected nodesSelectionMode: NodeSelectionMode;
  @ViewChild("messageTemplate") messageTemplate: TemplateRef<any>;
  public message: Message;
  public textFitInScreen: boolean;
  public containsAttachments: boolean;
  @Input() isVerticalMode: boolean = false;

  constructor(protected renderer: Renderer2,
              protected el: ElementRef,
              protected bpmnViewerService: BpmnViewerService,
              protected guidedBoxService: BpmnGuidedBoxService,
              protected store$: Store<State>,
              protected _changeDetector: ChangeDetectorRef,
              protected userClicksElementsService: UserClicksElementsService,
              protected messageService: MessageService,
              protected testService : TestService) {
    this.spinnerActive = true;
    this.userSelectionInProgress = this.store$.pipe(select(selectUserSelectionInProgress),takeUntil(this.destroyed$));
  }

  ngOnInit(): void {
    this.testConfiguration = this.testService.testConfiguration;
    if(this.testConfiguration){
      this.treatmentNotesGuideBox = !!this.testConfiguration.treatmentNotesGuideBox;
      this.showMultipleGuideBox = !!this.testConfiguration.showMultipleGuideBox;
      this.messageCount = (this.showMultipleGuideBox && this.testConfiguration.messageCount ? this.testConfiguration.messageCount : this.messageCount);
    }

  }

  protected getBpmnServices(): void {
    this.viewer = this.bpmnViewerService.getBpmnViewer();
  }

  ngAfterViewInit(): void {
    this.store$.dispatch(new MarkBpmnXmlParsingInProgressAction());
    this.getBpmnServices();
    this.populateGuidBoxes();
    this.store$.pipe(select(selectNodeSelectionMode), takeUntil(this.destroyed$.asObservable()))
      .subscribe(nodesSelectionMode => this.nodesSelectionMode = nodesSelectionMode);
    const bpmnProcessXml$: Observable<string> = this.store$.pipe(select(selectBpmnProcessXml), takeUntil(this.destroyed$.asObservable()));
    const nodesNamesSuffixes$: Observable<any> =
      this.store$.pipe(select(selectNodesNamesSuffixes), takeUntil(this.destroyed$.asObservable()));
    const xmlStatus$: Observable<any> = this.store$.pipe(select(selectGetBpmnXmlParsingStatus), takeUntil(this.destroyed$.asObservable()));

    const combinedObservable = combineLatest(bpmnProcessXml$, nodesNamesSuffixes$, xmlStatus$);
    combinedObservable.subscribe(
      ([xml, names, xmlParsingStatus]) => {
        if (xmlParsingStatus !== BP_Status.Completed && xml && names) {
          this.viewer.importXML(xml, this.handleXMLImportResponse.bind(this));
        }
      });
  }

  private executionPathUpdated(executionPath) {
    if (executionPath) {
      this.clearExecutedPath();
      this.executionPath = executionPath;
      this.navigateToFromNeighborhood();
      this.dimNodes();
      this.updateExecutedPath();
      this.treePathNavigator.recenter(executionPath, this.firstLoad);
      if (executionPath.executedTreatmentTaskId) {
        if(!this.showMultipleGuideBox){
          this.handleMandatoryGuidBox(this.executionPath.executedNodes.slice(-1)[0].nodeId, true, executionPath.executedTreatmentTaskId);
        }
        else{
          this.handleMultipleMandatoryGuidBox(this.executionPath.executedNodes.slice(-1)[0].nodeId, true, executionPath.executedTreatmentTaskId);
        }
      } else {
        if(!this.showMultipleGuideBox){
          this.handleMandatoryGuidBox(executionPath.currentUserTaskHolder.nodeId);
        }else{
          this.handleMultipleMandatoryGuidBox(executionPath.currentUserTaskHolder.nodeId);
        }
      }
      this.previousExecutionPath = this.executionPath;
      this._changeDetector.markForCheck();
      this.firstLoad = false;
    }
  }

  private navigateToFromNeighborhood() {
    const neighbHeadNodeOnThePath: Node[] = ExecutedPathUtils.findNodesAtPathByType(this.executionPath, NodeType.neighborhood_head);
    const neighbEntryNodeOnThePath: Node[] = ExecutedPathUtils.findNodesAtPathByType(this.executionPath, NodeType.neighborhood_entry);
    const currentNeighbHeadNodeId = this.treeNavigator.getCurrentNeighborhoodHeadNodeId();
    // we are at main tree or neighb and need to stay there
    if ((currentNeighbHeadNodeId == null && _.isEmpty(neighbHeadNodeOnThePath)) ||
      currentNeighbHeadNodeId && !_.isEmpty(neighbHeadNodeOnThePath) && neighbHeadNodeOnThePath[0].nodeId === currentNeighbHeadNodeId) {
      return;
    }
    this.firstLoad = true;
    // we are at neighb and need to navigate to main tree
    if (currentNeighbHeadNodeId && _.isEmpty(neighbHeadNodeOnThePath)) {
      this.treeNavigator.navigateToMainTree();
      this.treePathNavigator.recenterMainTreeExitingNeighborhood(this.executionPath);
      return;
    }
    const entryShape: Shape = this.getShapeByNode(neighbEntryNodeOnThePath[0]);
    // we are at main tree and need to insert neighb
    if (currentNeighbHeadNodeId == null && !_.isEmpty(neighbHeadNodeOnThePath)) {
      this.treeNavigator.navigateToSubTree(entryShape);
      return;
    }
    // we are at neighb and need to navigate to another
    if (currentNeighbHeadNodeId && !_.isEmpty(neighbHeadNodeOnThePath) && neighbHeadNodeOnThePath[0].nodeId !== currentNeighbHeadNodeId) {
      this.treeNavigator.navigateToMainTree();
      this.treeNavigator.navigateToSubTree(entryShape);
    }
  }

  private dimNodes() {
    this.dimNodesService.dimNodes(this.executionPath);
  }

  protected getShapeByNode(node: Node): Shape {
    return (node) ? this.getShapeByID(node.nodeId) : null;
  }

  protected getShapeByID(nodeId: string): Shape {
    return (nodeId) ? this.viewer.elementRegistry().get(nodeId) : null;
  }

  protected isMarkToBeRendered(nodeId: string): boolean {
    return _.get(this.getShapeByID(nodeId), 'businessObject.markedToBeRendered', false);
  }

  ngOnDestroy(): void {
    this.guidedBoxService.dismiss();
    this.bpmnViewerService.deleteViewer();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  handleXMLImportResponse(err: any) {
    this.spinnerActive = false;
    this._changeDetector.markForCheck();
    if (err) {
      console.warn('Ups, error: ', err);
    } else {
      this.store$.dispatch(new MarkBpmnXmlParsingCompletedAction());
      this.store$.pipe(
        select(selectExecutionPath),
        takeUntil(this.destroyed$.asObservable())
      ).subscribe(executionPath => this.executionPathUpdated(executionPath));
    }
  }

  get treeNavigator(): TreeNavigator {
    return this.viewer.get(InjectionNames.treeNavigator);
  }

  get treePathNavigator(): TreePathNavigator {
    return this.viewer.get(InjectionNames.treePathNavigator);
  }

  get dimNodesService(): DimNodesService {
    return this.viewer.get(InjectionNames.dimNodesService);
  }

  get zoomService(): ZoomService {
    return this.viewer.get(InjectionNames.zoomService);
  }

  get treeToScreenFitter(): TreeToScreenFitter {
    return this.viewer.get(InjectionNames.treeToScreenFitter);
  }

  public recenter() {
    this.navigateToFromNeighborhood();
    this.treePathNavigator.recenter(this.executionPath, false);
  }

  public topView() {
    this.treeToScreenFitter.fit();
  }

  private markExecutedPath(cssClass: string, methodName: string, executionPath: ExecutionPath) {
    this.updateConnectionsCsss(methodName, executionPath.executedConnectionsIds, cssClass);
    this.updateNodesCsss(methodName, this.getExecutedNodesIds(executionPath), cssClass);
    this.updateGatewaysCsss(methodName, [...executionPath.executedGatewaysIds], cssClass);
  }

  private markShapesToSelect(cssClass: string, methodName: string, executionPath: ExecutionPath) {
    if (this.nodesSelectionMode === NodeSelectionMode.directChildren) {
      this.updateNodesCsss(methodName, executionPath.nodesForSelection.map(node => node.nodeId), cssClass);
      this.updateConnectionsCsss(methodName, [...executionPath.connectionsToActivitiesForSelectionIds], cssClass);
      this.updateGatewaysCsss(methodName,
        [...(executionPath.gatewayIdToNodesToSelect ? [executionPath.gatewayIdToNodesToSelect] : [])], cssClass);
      this.updateNodesCsss(
        methodName,
        this.userClicksElementsService.getAllSelectableNodes(executionPath).map(node => node.nodeId));
    }
  }

  private getExecutedNodesIds(executionPath: ExecutionPath): string[] {
    const executedNodesIds =
      executionPath.executedNodes ? executionPath.executedNodes.map(node => node.nodeId) : [];
    if (executionPath.currentUserTaskHolder) {
      executedNodesIds.push(executionPath.currentUserTaskHolder.nodeId);
    }
    executedNodesIds.push(executionPath.executedTreatmentTaskId);
    return executedNodesIds.filter(id => id != null);
  }

  private updateConnectionsCsss(methodName: string, ids: string[], cssClass: string) {
    this.updateElementsCssClasses(methodName, ids, cssClass);
  }

  private updateNodesCsss(methodName: string, ids: string[], cssClass?: string) {
    this.updateElementsCssClasses(methodName, ids, cssClass);
  }

  private updateGatewaysCsss(methodName: string, ids: string[], cssClass: string) {
    this.updateElementsCssClasses(methodName, ids, cssClass);
  }

  private getDOMElement(id: string) {
    return this.el.nativeElement.querySelector(`[data-element-id=${id}]`);
  }

  protected updateElementsCssClasses(methodName: string, ids: string[], cssClass: string) {
    for (const id of ids) {
      if (this.isMarkToBeRendered(id)) {
        this.updateElementCssClass(methodName, this.getDOMElement(id), cssClass);
      }
    }
  }

  private updateElementCssClass(methodName: string, element: any, cssClass: string) {
    if (!element) {
      this.store$.dispatch(new SetErrorAction({
        error: 'Cannot find element to update',
        devMessage: 'updateCSSClass: element to be updated is not defined',
        userMessage: failedToFindElementMessage
      }));
    } else {
      this.renderer[methodName](element, cssClass?? '');
    }
  }

  public clearExecutedPath() {
    if (this.previousExecutionPath) {
      this.markExecutedPath(this.executedShapeCssClass, this.removeCssClassMethodName, this.previousExecutionPath);
      this.markShapesToSelect(this.selectionRequiredCssClass, this.removeCssClassMethodName, this.previousExecutionPath);
    }
  }

  private updateExecutedPath() {
    this.markExecutedPath(this.executedShapeCssClass, this.addCssClassMethodName, this.executionPath);
    this.markShapesToSelect(this.selectionRequiredCssClass, this.addCssClassMethodName, this.executionPath);
  }

  private populateGuidBoxes() {
    this.viewer.get(InjectionNames.eventBus).on(EventType.GUIDEBOX_OVERLAY_ADDED, event => this.handleGuideBox(event));
  }

  private handleGuideBox(payload: any) {
    const htmlElement = this.getGuideBoxHtmlElement(payload.elementId);
    htmlElement.addEventListener('click', (event) => {
      this.getGuidedBoxData(payload.elementId)
        .take(1)
        .subscribe((guideBoxData: GuideBox) => {
          if (!!guideBoxData) {
            guideBoxData.attachments = guideBoxData.attachments ? guideBoxData.attachments : [];
            this.openGuideBoxData(htmlElement, guideBoxData);
            this._changeDetector.markForCheck();
          } else {
            this.store$.dispatch(new SetErrorAction({
              error: 'data of clicked guideBox is undefined',
              devMessage: 'GuideBox: GuideBox icon exist and clicked by User but no guideBox data was found in store',
              userMessage: failedToLoadGuideboxesMessage
            }));
          }
        });
    });
  }

  protected openGuideBoxData(htmlElement, data: GuideBox) {
    const guideBoxPayload = new GuidedBoxPayload(data);
    let message = {
      template: this.messageTemplate,
      buttons: [
        {
          label: 'Show More',
          action: () => this.guidedBoxService.showFullNote(data)
        },
        {
          label: 'Close',
          action: () => this.messageService.clearMessage(message)
        }],
      body: undefined,
      classNames: undefined,
      hasAttachments : guideBoxPayload.containsAttachments()
    };

    if (guideBoxPayload.textIsFitInScreen()) {
      this.textFitInScreen = true;
      message.body = data.guidingText;
      if (guideBoxPayload.needShowMore()) {
        message.body =  guideBoxPayload.getFirstSentence();
      } else {
        message.buttons = [{
          label: 'Close',
          action: () => this.messageService.clearMessage(message)
        }]
        message.classNames = 'guide-box-preview guide-box-preview-without-attachments';
      }
    } else {
      this.textFitInScreen = false;
    }

    if(this.showMultipleGuideBox){
      if(!message.classNames)
        message.classNames = 'guide-box-preview ' + (this.isVerticalMode ? 'vertical-view-mode' : 'horizontal-view-mode') + ' pt-notification-item-slim';
      else
        message.classNames += ' pt-notification-item-slim';
    }

    this.guidedBoxService.openGuideBox(htmlElement, data, message, this.isVerticalMode);
  }

  private getGuidedBoxData(id: string): Observable<any> {
    return this.store$.select(selectGuidedBoxDataById(id));
  }
  private getGuidedBoxDataByIds(ids: string[]): Observable<any> {
    return this.store$.select(selectGuidedBoxDataByIds(ids));
  }

  private getTreatmentNodeGuidBoxData(id: string): Observable<any> {
    return this.store$.select(selectTreatmentNodeDataById(id));
  }

  private getTreatmentsName(data: any): string {
    return data.treatmentPlans.map(item => item.treatmentPlan.display).join(', ');
  }

  private handleMandatoryGuidBox(elementId: string, isTreatmentNode = false, executedTreatmentTaskId = '') {
    this.getGuidedBoxData(elementId).take(1).subscribe((guideBoxData: GuideBox) => {
      if (guideBoxData && guideBoxData.automatically && !ModelingUtil.isGuideBoxEmpty(guideBoxData)) {
        guideBoxData.attachments = guideBoxData.attachments ? guideBoxData.attachments : [];
        this.openGuideBoxData(this.getGuideBoxHtmlElement(elementId), guideBoxData);
        this.store$.dispatch(new MandatoryNoteVisitedAction({elementId}));
      } else {
        if (isTreatmentNode && this.treatmentNotesGuideBox) {
          this.handleTreatmentNodeGuideBox(executedTreatmentTaskId);
        } else {
          this.guidedBoxService.dismiss();
        }
      }
    });
  }

  private openMultipleGuideBoxData(elementIds: string[]){
    this.getGuidedBoxDataByIds(elementIds).subscribe( (returnedData :GuideBox[]) => {
      let notesData = {...returnedData};
      for (const elementId in notesData) {
        if (notesData.hasOwnProperty(elementId)) {
          notesData[elementId].attachments = notesData[elementId].attachments ? notesData[elementId].attachments : [];
          this.openGuideBoxData(this.getGuideBoxHtmlElement(elementId), notesData[elementId]);
          this.store$.dispatch(new MandatoryNoteVisitedAction({elementId}));
        }
      }
    });
  }

  private handleMultipleMandatoryGuidBox(elementId: string, isTreatmentNode = false, executedTreatmentTaskId = '') {
    let nodeIds = [];

    if(isTreatmentNode){
      nodeIds = [...this.executionPath.executedNodes.filter(node => node.nodeId !== "head_node_id").map(node => node.nodeId)];
      this.handleTreatmentNodeGuideBox(executedTreatmentTaskId);
    }else{
      nodeIds = [...this.executionPath.executedNodes.filter(node => node.nodeId !== "head_node_id").map(node => node.nodeId),this.executionPath.currentUserTaskHolder.nodeId];
    }
    this.guidedBoxService.dismiss();
    if(nodeIds && nodeIds.length){
      this.openMultipleGuideBoxData(nodeIds);
      if (isTreatmentNode && this.treatmentNotesGuideBox) {
        this.handleTreatmentNodeGuideBox(executedTreatmentTaskId);
      }
    }
  }

  private handleTreatmentNodeGuideBox(elementId: string) {
    this.getTreatmentNodeGuidBoxData(elementId).take(1).subscribe(node => {
      if (node && node.data) {
        this.guidedBoxService.showBaseGuideBox(
          {automatically: false, guidingText: this.getTreatmentsName(node.data), classNames : this.showMultipleGuideBox ? "pt-notification-item-slim" : ""});
      }
    });
  }

  private getGuideBoxHtmlElement(elementId: string): any {
    return this.el.nativeElement.querySelector('#guidebox_icon_' + elementId);
  }

  zoomOutCanvas() {
    this.zoomService.zoomOutCanvas();
  }

  zoomInCanvas() {
    this.zoomService.zoomInCanvas();
  }
}
